import { useEffect, useState, useMemo } from "react"
import PropTypes from "prop-types"
import { get, find } from "lodash"
import OPTooltip from "components/material/tooltip"
import { IconButton, Popover } from "@mui/material"
import { AutoFixHigh } from "@mui/icons-material"
import classNames from "classnames"
import { track } from "helpers/analytics"
import { DATE } from "helpers/shapes"
import { useModels } from "resources/models"
import FeedbackForm from "components/feedback-form"
import useLocalStorageSummaries from "../hooks/useLocalSummaries"
import useBaseResource from "../hooks/useBaseResource"
import useAISummaryState from "../hooks/useAISummaryState"
import usePopout from "../hooks/usePopout"
import useSummaryButtons from "../hooks/useSummaryButtons"
import Summary from "./summary"
import { TRACKING_EVENTS, ACTIONS } from "../constants"
import { RESOURCE, SUMMARY, BASE, ACTION, EXTRA_PARAMETERS } from "../shapes"
import Header from "./header"

export default function AISummary({ account, action, resource, startDate, endDate, period, isIntradayMode, latestDate, modelId, base, generate, addNotification, summaryKey, tooltip, extraParameters, generatedSummaries }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const isGeneratedByAI = action !== ACTIONS.SIMULATOR_SUMMARY

  const [models] = useModels()
  const model = find(models, { id: modelId })
  const baseWithName = useBaseResource(account, modelId, base, endDate)

  const { timestamp, summary, error, thumb, generateSummary } = useAISummaryState({ account, model, resource, startDate, endDate, period, isIntradayMode, latestDate, base, generate, extraParameters, generatedSummaries, action })

  // Get the summary matching summaryKey from localStorage
  const [summaries, setLocalStorageSummary, localStorageError] = useLocalStorageSummaries()
  const localStorageSummary = useMemo(() => get(summaries, summaryKey, null), [summaries, summaryKey])

  useEffect(() => {
    if (localStorageError) {
      addNotification("warning", null, "Unable to save AI generated summary due to browser storage limits. Try clearing your browser data.")
    }
  }, [addNotification, localStorageError])

  const summaryInputs = useMemo(() => ({ resource, startDate, endDate, model, base: baseWithName }), [resource, startDate, endDate, model, baseWithName])
  const trackingProps = useMemo(() => ({ summaryType: action, resource }), [action, resource])
  const feedbackTrackingProps = { direction: thumb, ...summaryInputs }

  const { popoutButton } = usePopout({ account, summaryKey, summaryInputs, addNotification, localStorageError })

  function handleClick(event) {
    setAnchorEl(anchorEl ? null : event.currentTarget)
    track(TRACKING_EVENTS.OPENED, trackingProps)
  }

  const isLoading = !summary && !error
  const isGenerated = Boolean(timestamp && summary) && !isLoading
  const isPopupOpen = Boolean(anchorEl)

  useEffect(() => {
    if (isPopupOpen && isGenerated) {
      track(TRACKING_EVENTS.VIEWED, trackingProps)
    }
  }, [isPopupOpen, isGenerated, trackingProps])

  const headerButtons = [
    popoutButton,
  ]

  const summaryButtons = useSummaryButtons({
    isGenerated,
    isLoading,
    thumb,
    summary,
    error,
    resource,
    summaryKey,
    localStorageSummary,
    localStorageError,
    setLocalStorageSummary,
    generateSummary,
    addNotification,
    action,
    setFeedbackOpen,
  })

  let title = tooltip ? tooltip : "Open Summary"
  if (!isGenerated) {
    title = "Summarize"
  }

  return <div className="modules_ai-summary">
    <OPTooltip title={title}>
      <IconButton color="primary" size="large" onClick={handleClick} className={classNames("generate-button", { loading: isLoading, loaded: isGenerated })}>
        <AutoFixHigh />
      </IconButton>
    </OPTooltip>

    <Popover
      id="ai-summary-popover"
      open={isPopupOpen}
      anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      {isPopupOpen && <>
        <Header {...{ headerButtons, isGenerated, timestamp, resource, model, startDate, endDate, base: baseWithName, showDateRange: isGeneratedByAI }} />
        <Summary className="without-tabs" {...{ isGenerated, headerButtons, summaryButtons, summary, timestamp, error, startDate, endDate, model, base: baseWithName, summaryKey, isGeneratedByAI, showDateRange: isGeneratedByAI }} />
      </>}
    </Popover>
    <FeedbackForm
      open={feedbackOpen}
      onClose={() => setFeedbackOpen(false)}
      feedbackTrackingProps={feedbackTrackingProps}
      trackingEvent={TRACKING_EVENTS.ADDITIONAL_FEEDBACK}
      getPlaceholderText={(direction) => `Why was this summary ${direction === "positive" ? "helpful" : "unhelpful"}?`}
    />
  </div>
}

AISummary.propTypes = {
  action: ACTION,
  summaryKey: PropTypes.string.isRequired,
  account: PropTypes.string.isRequired,
  selectedDate: DATE,
  startDate: DATE,
  endDate: DATE,
  period: PropTypes.string,
  isIntradayMode: PropTypes.bool,
  latestDate: DATE,
  modelId: PropTypes.string.isRequired,
  base: BASE,
  generatedSummary: SUMMARY,
  generate: PropTypes.func.isRequired,
  resource: RESOURCE,
  addNotification: PropTypes.func.isRequired,
  tooltip: PropTypes.string,
  extraParameters: EXTRA_PARAMETERS,
  generatedSummaries: PropTypes.objectOf(SUMMARY),
}
